import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useFormattedNumber } from '../hooks/useFormattedNumber';
import Breadcrumb from './Breadcrumb';
import LoadingSpinner from './LoadingSpinner';
import CloneScenarioModal from './CloneScenarioModal';
import GoalSeekModal from './GoalSeekModal';
import { useAuth } from '../contexts/AuthContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ScenarioView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [scenario, setScenario] = useState({});
  const [isModified, setIsModified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showGoalSeekModal, setShowGoalSeekModal] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [error, setError] = useState(null);
  const [rentalType, setRentalType] = useState('long-term'); // 'long-term' or 'short-term'
  const [occupancyRate, setOccupancyRate] = useState('65'); // Default to 65% occupancy for short-term rentals
  const yearsPerPage = 10;
  const totalPages = 3; // 30 years total
  
  // Initialize form state
  const [name, setName] = useState('');
  const priceFormat = useFormattedNumber('');
  const [downPaymentPercent, setDownPaymentPercent] = useState('');
  const closingCostsFormat = useFormattedNumber('');
  const [interestRate, setInterestRate] = useState('');
  const [term, setTerm] = useState('30');
  const [isCashPurchase, setIsCashPurchase] = useState(false);
  const monthlyRentFormat = useFormattedNumber('');
  const nightlyRateFormat = useFormattedNumber('');
  const [rentGrowth, setRentGrowth] = useState('');
  const homeValueFormat = useFormattedNumber('');
  const repairEstimatesFormat = useFormattedNumber('');
  const [appreciation, setAppreciation] = useState('');
  const [useArvForCapRate, setUseArvForCapRate] = useState(false);
  const monthlyInsuranceFormat = useFormattedNumber('');
  const propertyTaxFormat = useFormattedNumber('');
  const [capExRate, setCapExRate] = useState('');
  const monthlyHoaFormat = useFormattedNumber(''); // Keep for backward compatibility
  const otherExpensesFormat = useFormattedNumber('');
  const [managementFee, setManagementFee] = useState('');
  const [vacancyRate, setVacancyRate] = useState('');
  const [maintenanceRate, setMaintenanceRate] = useState('');

  // Save button state
  const [calculatedValues, setCalculatedValues] = useState({
    monthlyMortgage: 0,
    cashToClose: 0,
    calculations: {
      // Metrics
      capRate: 0,
      annualCashFlow: 0,
      annualCashOnCashReturn: 0,
      dscr: 0,
      annualROI: 0,
      
      // Annual values
      annualGrossRent: 0,
      annualExpenses: 0,
      annualInsurance: 0,
      annualMaintenance: 0,
      annualManagement: 0,
      annualMortgage: 0,
      annualTaxes: 0,
      annualVacancy: 0
    }
  });

  const [isFocused, setIsFocused] = useState({});
  const [propertyTargets, setPropertyTargets] = useState({
    targetCapRate: null,
    targetCoCReturn: null,
    targetTotalROI: null
  });
  
  // Reference to store the table data for saving
  const tableDataRef = useRef({
    cashFlowPercentByYear: Array(10).fill(0),
    cumulativeROIByYear: Array(10).fill(0)
  });
  
  // Flag to track if we need to update chart projections after initial render
  const [needsChartUpdate, setNeedsChartUpdate] = useState(false);
  
  // Function to update chart projections for new scenarios
  const updateChartProjections = async () => {
    if (!id || !tableDataRef.current) return;
    
    try {
      console.log('Updating chart projections for new scenario');
      
      // Get the current data from the table
      const cashOnCashData = [...tableDataRef.current.cashFlowPercentByYear];
      const totalROIData = [...tableDataRef.current.cumulativeROIByYear];
      
      // Log the values we're using for debugging
      console.log('Using chart data directly:', { cashOnCashData, totalROIData });
      
      // First get the current scenario data to preserve all fields
      const docRef = doc(db, 'scenarios', id);
      
      // Only update the specific fields we need to change, not the entire document
      await updateDoc(docRef, {
        chartProjections: {
          cashOnCash: cashOnCashData,
          totalROI: totalROIData
        },
        updatedAt: new Date().toISOString()
      });
      
      console.log('Chart projections updated successfully');
      setNeedsChartUpdate(false);
    } catch (error) {
      console.error('Error updating chart projections:', error);
    }
  };

  useEffect(() => {
    const fetchScenario = async (retryCount = 0) => {
      try {
        setIsLoading(true);
        setError(null);
        const docRef = doc(db, 'scenarios', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setScenario(data);
          
          // Fetch property data to get target metrics
          if (data.houseId) {
            try {
              const propertyDocRef = doc(db, 'properties', data.houseId);
              const propertyDocSnap = await getDoc(propertyDocRef);
              
              if (propertyDocSnap.exists()) {
                const propertyData = propertyDocSnap.data();
                // Set the target metrics
                setPropertyTargets({
                  targetCapRate: propertyData.targetCapRate,
                  targetCoCReturn: propertyData.targetCoCReturn,
                  targetTotalROI: propertyData.targetTotalROI
                });
              }
            } catch (error) {
              console.error('Error fetching property data:', error);
            }
          }
          
          // Set all the form values with the correct field names from AddScenarioModal
          setName(data.name || ''); // Set the scenario name
          priceFormat.onChange({ target: { value: data.price?.toString() || '0' } });
          setIsCashPurchase(data.isCashPurchase || false);
          setDownPaymentPercent(data.downPaymentPercent?.toString() || '20');
          setInterestRate(data.interestRate?.toString() || '6.5');
          setTerm(data.loanTermYears?.toString() || '30');
          closingCostsFormat.onChange({ target: { value: data.closingCosts?.toString() || '0' } });
          
          // Check if this is a new scenario that needs chart projections to be updated
          // We consider it new if all chart projection values are 0
          if (data.chartProjections) {
            const allZeroCashOnCash = data.chartProjections.cashOnCash?.every(val => val === 0) || false;
            const allZeroTotalROI = data.chartProjections.totalROI?.every(val => val === 0) || false;
            
            if (allZeroCashOnCash && allZeroTotalROI) {
              console.log('New scenario detected, will update chart projections after render');
              setNeedsChartUpdate(true);
            }
          }
          setRentalType(data.rentalType || 'long-term');
          
          // Set occupancy rate for short-term rentals (default to 65% if not specified)
          setOccupancyRate(data.occupancyRate?.toString() || '65');
          setIsCashPurchase(data.isCashPurchase || false);
          
          // Initialize rent values based on rental type
          if (data.rentalType === 'short-term') {
            // For short-term rentals, use nightlyRate
            nightlyRateFormat.onChange({ target: { value: data.nightlyRate?.toString() || data.monthlyRent?.toString() || '' } });
            // Still initialize monthlyRent for backward compatibility
            monthlyRentFormat.onChange({ target: { value: data.monthlyRent?.toString() || '' } });
          } else {
            // For long-term rentals, use monthlyRent
            monthlyRentFormat.onChange({ target: { value: data.monthlyRent?.toString() || '' } });
            // Initialize nightlyRate with saved value or empty
            nightlyRateFormat.onChange({ target: { value: data.nightlyRate?.toString() || '' } });
          }
          setRentGrowth(data.rentGrowth?.toString() || '');
          homeValueFormat.onChange({ target: { value: data.initialHomeValue?.toString() || data.price?.toString() || '' } });
          setAppreciation(data.appreciation?.toString() || '');
          // Load the useArvForCapRate toggle state
          setUseArvForCapRate(data.useArvForCapRate || false);
          monthlyInsuranceFormat.onChange({ target: { value: data.monthlyInsurance?.toString() || '' } });
          propertyTaxFormat.onChange({ target: { value: data.propertyTax?.toString() || '' } });
          setCapExRate(data.capExRate?.toString() || ''); // Load CapEx rate instead of HOA
          otherExpensesFormat.onChange({ target: { value: data.otherExpenses?.toString() || '' } });
          setManagementFee(data.managementFee?.toString() || '');
          setVacancyRate(data.vacancyRate?.toString() || '');
          setMaintenanceRate(data.maintenanceRate?.toString() || '');
          repairEstimatesFormat.onChange({ target: { value: data.repairEstimates?.toString() || '' } });

          // Calculate initial metrics
          const monthlyMortgage = (() => {
            const principal = data.price * (1 - Number(data.downPaymentPercent) / 100);
            const monthlyRate = (Number(data.interestRate) / 100) / 12;
            const numberOfPayments = Number(data.loanTermYears || 30) * 12;
            
            // Use the standard amortizing loan formula
            const payment = principal * 
              (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
              (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
            
            return isNaN(payment) ? 0 : payment;
          })();

          // Calculate yearly gross rent based on rental type
          let yearlyGrossRent = 0;
          let grossPotentialIncome = 0;
          
          if (data.rentalType === 'short-term') {
            // For short-term rentals, calculate gross potential income (100% occupancy)
            const nightlyRate = Number(data.nightlyRate || 0);
            grossPotentialIncome = nightlyRate * 365;
            
            // Calculate actual gross rent based on occupancy rate
            const occupancyRateDecimal = Number(data.occupancyRate || 65) / 100;
            yearlyGrossRent = nightlyRate * 365 * occupancyRateDecimal;
          } else {
            // For long-term rentals, calculate gross potential income (100% occupancy)
            grossPotentialIncome = Number(data.monthlyRent || 0) * 12;
            
            // Calculate actual gross rent after vacancy
            const vacancyRateDecimal = Number(data.vacancyRate || 0) / 100;
            yearlyGrossRent = grossPotentialIncome * (1 - vacancyRateDecimal);
          }
          
          const yearlyMortgage = monthlyMortgage * 12;
          const yearlyInsurance = Number(data.monthlyInsurance) * 12;
          const yearlyTaxes = Number(data.propertyTax);
          const yearlyOtherExpenses = Number(data.otherExpenses || 0) * 12; // Ensure this is annual
          
          // Calculate percentage-based expenses from gross potential income (before vacancy)
          const yearlyManagement = grossPotentialIncome * (Number(data.managementFee) / 100);
          const yearlyMaintenance = grossPotentialIncome * (Number(data.maintenanceRate) / 100);
          const yearlyCapEx = grossPotentialIncome * (Number(data.capExRate || 0) / 100);
          
          // Calculate vacancy separately
          const yearlyVacancy = grossPotentialIncome * (Number(data.vacancyRate || 0) / 100);
          
          // Calculate total operating expenses (excluding vacancy)
          const operatingExpenses = yearlyInsurance + yearlyTaxes + yearlyOtherExpenses + yearlyManagement + yearlyMaintenance + yearlyCapEx;
          
          // Calculate NOI: Gross Potential Income - Vacancy - Operating Expenses
          // Note: yearlyGrossRent already has vacancy deducted, so we use: yearlyGrossRent - operatingExpenses
          const noi = yearlyGrossRent - operatingExpenses;
          
          // Calculate cash flow: NOI - Mortgage
          const cashFlow = noi - yearlyMortgage;
          
          // Total expenses for reporting purposes
          const totalExpenses = Number((operatingExpenses + yearlyVacancy).toFixed(2));
          
          const totalCashInvested = (Number(data.price) * (Number(data.downPaymentPercent) / 100)) + 
                                   Number(data.closingCosts) +
                                   Number(data.repairEstimates || 0);
                                   
          const cashFlowPercent = totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : 0;
          
          const initialValue = Number(data.initialHomeValue) || Number(data.price) || 0;
          const capRate = initialValue > 0 ? (noi / initialValue) * 100 : 0;
          
          // Calculate appreciation using the same method as yearly returns
          const appreciationRate = Number(data.appreciation) / 100;
          const currentYearValue = initialValue * (1 + appreciationRate);
          const yearlyAppreciationAmount = currentYearValue - initialValue;
          const appreciationPercent = totalCashInvested > 0 ? (yearlyAppreciationAmount / totalCashInvested) * 100 : 0;
          
          // Calculate principal paydown for first year
          let yearlyPrincipalPaydown = 0;
          let principalPaydownPercent = 0;
          
          if (!data.isCashPurchase) {
            // Only calculate principal paydown for regular amortizing loans
            let remainingBalance = Number(data.price || 0) * (1 - Number(data.downPaymentPercent || 0) / 100);
            const startOfYearBalance = remainingBalance;
            const monthlyRate = (Number(data.interestRate || 0) / 100) / 12;
            
            // Calculate first year's principal paydown
            for (let month = 1; month <= 12; month++) {
              const interestPayment = remainingBalance * monthlyRate;
              const principalPayment = monthlyMortgage - interestPayment;
              remainingBalance = remainingBalance - principalPayment;
            }
            
            yearlyPrincipalPaydown = startOfYearBalance - remainingBalance;
            principalPaydownPercent = totalCashInvested > 0 ? (yearlyPrincipalPaydown / totalCashInvested) * 100 : 0;
          }
          // For interest-only loans, principal paydown is 0
          
          const totalROI = appreciationPercent + cashFlowPercent + principalPaydownPercent;
          
          // Calculate DSCR (Debt Service Coverage Ratio)
          const dscr = data.isCashPurchase ? null : (yearlyMortgage > 0 ? noi / yearlyMortgage : 0);

          // Set initial calculated values
          setCalculatedValues({
            monthlyMortgage,
            cashToClose: (Number(data.price) * (Number(data.downPaymentPercent) / 100)) + Number(data.closingCosts),
            yearlyTotals: {
              grossRent: yearlyGrossRent || 0,
              yearlyMortgage: yearlyMortgage || 0,
              yearlyInsurance: yearlyInsurance || 0,
              yearlyTaxes: yearlyTaxes || 0,
              yearlyManagement: yearlyManagement || 0,
              yearlyVacancy: yearlyVacancy || 0,
              yearlyMaintenance: yearlyMaintenance || 0,
              totalExpenses: totalExpenses || 0,
              cashFlow: cashFlow || 0,
              cashOnCashReturn: cashFlowPercent || 0
            },
            keyMetrics: {
              totalROI,
              capRate,
              cashOnCashReturn: cashFlowPercent,
              cashFlow,
              dscr
            }
          });
        }
      } catch (error) {
        console.error('Error fetching scenario:', error);
        
        // For permission errors, retry a few times (user might have just created the scenario)
        if (error.code === 'permission-denied' && retryCount < 3) {
          console.log(`Permission denied, retrying (${retryCount + 1}/3)...`);
          setTimeout(() => fetchScenario(retryCount + 1), 1000 * (retryCount + 1));
          return;
        }
        
        setError(error.message || 'Failed to load scenario. You may not have permission to view this scenario.');
        setIsLoading(false);
        
        // If we can't access after retries, navigate back to properties
        if (retryCount >= 3) {
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchScenario();
  }, [id]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Calculate all metrics before saving
      const monthlyMortgage = (() => {
        const principal = Number(priceFormat.value) * (1 - Number(downPaymentPercent) / 100);
        const monthlyRate = (Number(interestRate) / 100) / 12;
        const numberOfPayments = Number(term) * 12;
        
        // Use the standard amortizing loan formula
        const payment = principal * 
          (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
          (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
        
        return isNaN(payment) ? 0 : payment;
      })();

      // Calculate yearly gross rent based on rental type
      let yearlyGrossRent = 0;
      let grossPotentialIncome = 0;
      
      if (rentalType === 'long-term') {
        // For long-term rentals, calculate gross potential income (100% occupancy)
        grossPotentialIncome = Number(monthlyRentFormat.value) * 12;
        
        // Calculate actual gross rent after vacancy
        const vacancyRateDecimal = Number(vacancyRate) / 100;
        yearlyGrossRent = grossPotentialIncome * (1 - vacancyRateDecimal);
      } else {
        // For short-term rentals, calculate gross potential income (100% occupancy)
        const nightlyRate = Number(nightlyRateFormat.value);
        grossPotentialIncome = nightlyRate * 365;
        
        // Calculate actual gross rent based on occupancy rate
        const occupancyRateDecimal = Number(occupancyRate) / 100;
        yearlyGrossRent = nightlyRate * 365 * occupancyRateDecimal;
      }
      
      const yearlyMortgage = monthlyMortgage * 12;
      const yearlyInsurance = Number(monthlyInsuranceFormat.value) * 12;
      const yearlyTaxes = Number(propertyTaxFormat.value);
      const yearlyOtherExpenses = Number(otherExpensesFormat.value || 0) * 12; // Monthly to yearly
      
      // Calculate percentage-based expenses from gross potential income (before vacancy)
      const yearlyManagement = grossPotentialIncome * (Number(managementFee) / 100);
      const yearlyMaintenance = grossPotentialIncome * (Number(maintenanceRate) / 100);
      const yearlyCapEx = grossPotentialIncome * (Number(capExRate) / 100);
      
      // Calculate vacancy separately
      const yearlyVacancy = grossPotentialIncome * (Number(vacancyRate) / 100);
      
      // Calculate total operating expenses (excluding vacancy)
      const operatingExpenses = yearlyInsurance + yearlyTaxes + yearlyOtherExpenses + yearlyManagement + yearlyMaintenance + yearlyCapEx;
      
      // Calculate NOI: Gross Potential Income - Vacancy - Operating Expenses
      // Note: yearlyGrossRent already has vacancy deducted, so we use: yearlyGrossRent - operatingExpenses
      const noi = yearlyGrossRent - operatingExpenses;
      
      // Calculate cash flow: NOI - Mortgage
      const cashFlow = noi - yearlyMortgage;
      
      // Total expenses for reporting purposes
      const totalExpenses = Number((operatingExpenses + yearlyVacancy).toFixed(2));
      
      // Calculate DSCR (Debt Service Coverage Ratio)
      const dscr = isCashPurchase ? null : (yearlyMortgage > 0 ? noi / yearlyMortgage : 0);
      
      // Calculate cash to close (down payment + closing costs, no repairs)
      const cashToClose = (Number(priceFormat.value) * (Number(downPaymentPercent) / 100)) + 
                         Number(closingCostsFormat.value);
      
      // Calculate total investment (cash to close + repairs)
      const totalCashInvested = cashToClose + Number(repairEstimatesFormat.value || 0);
                               
      const cashFlowPercent = totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : 0;
      
      // Determine which value to use for cap rate calculation based on toggle
      const arvValue = Number(homeValueFormat.value) || 0;
      const purchasePrice = Number(priceFormat.value) || 0;
      
      // If useArvForCapRate is true, use ARV; otherwise use purchase price
      const valueForCapRate = useArvForCapRate ? arvValue : purchasePrice;
      
      // Use ARV as fallback if purchase price is 0, and vice versa
      const initialValue = valueForCapRate || (useArvForCapRate ? purchasePrice : arvValue) || 0;
      const capRate = initialValue > 0 ? (noi / initialValue) * 100 : 0;
      
      // Calculate appreciation using the same method as yearly returns
      const appreciationRate = Number(appreciation) / 100;
      const currentYearValue = initialValue * (1 + appreciationRate);
      const yearlyAppreciationAmount = currentYearValue - initialValue;
      const appreciationPercent = totalCashInvested > 0 ? (yearlyAppreciationAmount / totalCashInvested) * 100 : 0;
      
      // Calculate principal paydown for first year
      let yearlyPrincipalPaydown = 0;
      let principalPaydownPercent = 0;
      
      if (!isCashPurchase) {
        // Only calculate principal paydown for regular amortizing loans
        let remainingBalance = Number(priceFormat.value || 0) * (1 - Number(downPaymentPercent || 0) / 100);
        const startOfYearBalance = remainingBalance;
        const monthlyRate = (Number(interestRate || 0) / 100) / 12;
        
        // Calculate first year's principal paydown
        for (let month = 1; month <= 12; month++) {
          const interestPayment = remainingBalance * monthlyRate;
          const principalPayment = monthlyMortgage - interestPayment;
          remainingBalance = remainingBalance - principalPayment;
        }
        
        yearlyPrincipalPaydown = startOfYearBalance - remainingBalance;
        principalPaydownPercent = totalCashInvested > 0 ? (yearlyPrincipalPaydown / totalCashInvested) * 100 : 0;
      }
      // For interest-only loans, principal paydown is 0
      
      const totalROI = appreciationPercent + cashFlowPercent + principalPaydownPercent;

      // Calculate new metrics
      const newCalculatedValues = {
        monthlyMortgage,
        cashToClose: cashToClose,
        calculations: {
          // Metrics
          capRate: capRate || 0,
          annualCashFlow: cashFlow || 0,
          annualCashOnCashReturn: cashFlowPercent || 0,
          dscr: dscr || 0,
          annualROI: totalROI || 0,
          
          // Annual values
          annualGrossRent: yearlyGrossRent || 0,
          annualExpenses: totalExpenses || 0,
          annualInsurance: yearlyInsurance || 0,
          annualMaintenance: yearlyMaintenance || 0,
          annualManagement: yearlyManagement || 0,
          annualMortgage: yearlyMortgage || 0,
          annualTaxes: yearlyTaxes || 0,
          annualVacancy: yearlyVacancy || 0
        }
      };

      // Always get the values directly from the table data reference without recalculation
      // This ensures we're using the exact values shown in the yearly returns chart
      const cashOnCashData = [...tableDataRef.current.cashFlowPercentByYear];
      const totalROIData = [...tableDataRef.current.cumulativeROIByYear];
      
      // Log the values we're using for debugging
      console.log('Using chart data directly:', { cashOnCashData, totalROIData });
      
      // We no longer need to recalculate these values as we're taking them directly from the chart
      // The following code is kept for reference but is now commented out
      /*
      // Calculate yearly projections for the first 10 years
      for (let year = 1; year <= 10; year++) {
        // Calculate metrics for each year (similar to the table calculation)
        const initialValue = Number(homeValueFormat.value) || Number(priceFormat.value) || 0;
        const appreciationRate = Number(appreciation) / 100;
        const rentGrowthRate = Number(rentGrowth) / 100;
        const totalCashInvested = (Number(priceFormat.value || 0) * (Number(downPaymentPercent || 0) / 100)) + 
                           Number(closingCostsFormat.value || 0) +
                           Number(repairEstimatesFormat.value || 0);

        // Calculate market rent for this year based on rental type
        let yearlyRent = 0;
        if (rentalType === 'short-term') {
          const nightlyRate = Number(nightlyRateFormat.value || 0);
          const occupancyRateDecimal = Number(occupancyRate || 65) / 100;
          const estimatedOccupancyDays = 365 * occupancyRateDecimal;
          yearlyRent = nightlyRate * estimatedOccupancyDays;
        } else {
          yearlyRent = Number(monthlyRentFormat.value || 0) * 12;
        }
        
        let yearlyInsurance = Number(monthlyInsuranceFormat.value) * 12;
        let yearlyTaxes = Number(propertyTaxFormat.value);
        
        // Compound rent, insurance, and taxes by growth rate
        for (let y = 1; y < year; y++) {
          yearlyRent *= (1 + rentGrowthRate);
          yearlyInsurance *= (1 + rentGrowthRate);
          yearlyTaxes *= (1 + rentGrowthRate);
        }

        // Calculate appreciation for this specific year
        let previousYearValue = initialValue;
        for (let y = 1; y < year; y++) {
          previousYearValue *= (1 + appreciationRate);
        }
        const currentYearValue = previousYearValue * (1 + appreciationRate);
        const yearlyAppreciationAmount = currentYearValue - previousYearValue;
        const appreciationPercent = totalCashInvested > 0 ? (yearlyAppreciationAmount / totalCashInvested) * 100 : 0;
        
        // Calculate metrics using the compounded rent and expenses
        let yearlyNOI = 0;
        
        if (rentalType === 'short-term') {
          yearlyNOI = yearlyRent - 
                      (yearlyRent * (Number(managementFee) / 100)) - 
                      (yearlyRent * (Number(maintenanceRate) / 100)) -
                      yearlyInsurance -
                      yearlyTaxes;
        } else {
          yearlyNOI = yearlyRent - 
                      (yearlyRent * (Number(vacancyRate) / 100)) - 
                      (yearlyRent * (Number(managementFee) / 100)) - 
                      (yearlyRent * (Number(maintenanceRate) / 100)) -
                      yearlyInsurance -
                      yearlyTaxes;
        }
        const yearlyMortgage = calculatedValues.monthlyMortgage * 12;
        const cashFlow = yearlyNOI - yearlyMortgage;
        const cashFlowPercent = totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : 0;
        
        // Calculate debt paydown using amortization schedule
        const loanAmount = Number(priceFormat.value || 0) * (1 - Number(downPaymentPercent || 0) / 100);
        const monthlyRate = (Number(interestRate || 0) / 100) / 12;
        const monthlyPayment = calculatedValues.monthlyMortgage;

        let remainingBalance = loanAmount;
        let startOfYearBalance = loanAmount;
        
        // Fast forward to current year's amortization
        for (let month = 1; month <= (year - 1) * 12; month++) {
          const interestPayment = remainingBalance * monthlyRate;
          const principalPayment = monthlyPayment - interestPayment;
          remainingBalance = remainingBalance - principalPayment;
        }
        startOfYearBalance = remainingBalance;
        
        // Calculate this year's principal paydown
        for (let month = 1; month <= 12; month++) {
          const interestPayment = remainingBalance * monthlyRate;
          const principalPayment = monthlyPayment - interestPayment;
          remainingBalance = remainingBalance - principalPayment;
        }
        
        const yearlyPrincipalPaydown = startOfYearBalance - remainingBalance;
        const principalPaydownPercent = totalCashInvested > 0 ? (yearlyPrincipalPaydown / totalCashInvested) * 100 : 0;
        
        const yearlyROI = appreciationPercent + cashFlowPercent + principalPaydownPercent;

        // Calculate cumulative ROI
        let cumulativeROI = 0;
        for (let y = 1; y <= year; y++) {
          // Calculate appreciation for this year
          let prevValue = initialValue;
          for (let py = 1; py < y; py++) {
            prevValue *= (1 + appreciationRate);
          }
          const currValue = prevValue * (1 + appreciationRate);
          const yearAppreciation = currValue - prevValue;
          const yearAppreciationPercent = totalCashInvested > 0 ? (yearAppreciation / totalCashInvested) * 100 : 0;

          // Calculate cash flow percent for this year
          let yearCashFlow = 0;
          let yearRent = Number(monthlyRentFormat.value || 0) * 12;
          let yearIns = Number(monthlyInsuranceFormat.value) * 12;
          let yearTax = Number(propertyTaxFormat.value);
          
          // Compound values for this specific year
          for (let cy = 1; cy < y; cy++) {
            yearRent *= (1 + rentGrowthRate);
            yearIns *= (1 + rentGrowthRate);
            yearTax *= (1 + rentGrowthRate);
          }
          
          let yearNOI = 0;
          if (rentalType === 'short-term') {
            yearNOI = yearRent - 
                     (yearRent * (Number(managementFee) / 100)) - 
                     (yearRent * (Number(maintenanceRate) / 100)) -
                     yearIns -
                     yearTax;
          } else {
            yearNOI = yearRent - 
                     (yearRent * (Number(vacancyRate) / 100)) - 
                     (yearRent * (Number(managementFee) / 100)) - 
                     (yearRent * (Number(maintenanceRate) / 100)) -
                     yearIns -
                     yearTax;
          }
          yearCashFlow = yearNOI - yearlyMortgage;
          const yearCashFlowPercent = totalCashInvested > 0 ? (yearCashFlow / totalCashInvested) * 100 : 0;

          // Calculate debt paydown for this year
          let yearRemBalance = loanAmount;
          let yearStartBalance = loanAmount;
          
          // Fast forward to this year
          for (let month = 1; month <= (y - 1) * 12; month++) {
            const intPayment = yearRemBalance * monthlyRate;
            const princPayment = monthlyPayment - intPayment;
            yearRemBalance = yearRemBalance - princPayment;
          }
          yearStartBalance = yearRemBalance;
          
          // Calculate this year's paydown
          for (let month = 1; month <= 12; month++) {
            const intPayment = yearRemBalance * monthlyRate;
            const princPayment = monthlyPayment - intPayment;
            yearRemBalance = yearRemBalance - princPayment;
          }
          
          const yearPrincPaydown = yearStartBalance - yearRemBalance;
          const yearPaydownPercent = totalCashInvested > 0 ? (yearPrincPaydown / totalCashInvested) * 100 : 0;

          // Add this year's ROI to cumulative
          cumulativeROI += yearAppreciationPercent + yearCashFlowPercent + yearPaydownPercent;
        }

        // Save the calculated values to the arrays (0-indexed)
        const arrayIndex = year - 1;
        cashOnCashData[arrayIndex] = Number(cashFlowPercent.toFixed(2));
        totalROIData[arrayIndex] = Number(cumulativeROI.toFixed(2));
      }
      */
      
      // We no longer need to override with yearly totals data since we're using the chart data directly
      // The following code is kept for reference but is now commented out
      /*
      // If we have yearly totals data, override with actual values
      if (newCalculatedValues.calculations) {
        // Process calculations for chart projections
          if (yearData) {
            // Year 1 data goes to index 0, Year 2 to index 1, etc.
            const arrayIndex = year - 1;
            
            // Cash on Cash Return
            if (yearData.cashFlowPercent !== undefined) {
              cashOnCashData[arrayIndex] = Number(yearData.cashFlowPercent.toFixed(2));
            }
            
            // Total ROI
            if (yearData.cumulativeROI !== undefined) {
              totalROIData[arrayIndex] = Number(yearData.cumulativeROI.toFixed(2));
            }
          }
        }
      }
      */
      
      // Update the scenario in Firestore
      const docRef = doc(db, 'scenarios', id);
      await updateDoc(docRef, {
        name,
        price: parseFloat(priceFormat.value),
        isCashPurchase,
        downPaymentPercent: parseFloat(downPaymentPercent),
        interestRate: parseFloat(interestRate),
        loanTermYears: parseInt(term),
        closingCosts: parseFloat(closingCostsFormat.value || 0),
        rentalType: rentalType,
        // Always store both monthly rent and nightly rate
        monthlyRent: parseFloat(monthlyRentFormat.value || 0),
        nightlyRate: parseFloat(nightlyRateFormat.value || 0),
        // Always store occupancy rate in its own field
        occupancyRate: parseFloat(occupancyRate || 65),
        rentGrowth: parseFloat(rentGrowth || 0),
        initialHomeValue: parseFloat(homeValueFormat.value || priceFormat.value),
        repairEstimates: parseFloat(repairEstimatesFormat.value || 0),
        appreciation: parseFloat(appreciation || 0),
        monthlyInsurance: parseFloat(monthlyInsuranceFormat.value || 0),
        propertyTax: parseFloat(propertyTaxFormat.value || 0),
        managementFee: parseFloat(managementFee || 0),
        vacancyRate: parseFloat(vacancyRate || 0),
        maintenanceRate: parseFloat(maintenanceRate || 0),
        capExRate: parseFloat(capExRate || 0),
        useArvForCapRate: useArvForCapRate, // Save the toggle state
        cashToClose: totalCashInvested,
        calculations: newCalculatedValues.calculations,
        chartProjections: {
          cashOnCash: cashOnCashData,
          totalROI: totalROIData
        },
        userId: currentUser.uid, // Ensure userId is set for permissions
        createdBy: scenario.createdBy || currentUser.uid, // Preserve original creator if exists
        updatedAt: new Date().toISOString()
      });

      // Update the property document with this scenario's chart projections
      if (scenario.houseId) {
        try {
          const propertyRef = doc(db, 'properties', scenario.houseId);
          const propertyDoc = await getDoc(propertyRef);
          
          if (propertyDoc.exists()) {
            // Get existing scenarios projections or initialize a new object
            const existingProjections = propertyDoc.data().scenarioProjections || {};
            
            // Update with this scenario's projections
            existingProjections[id] = {
              name: name,
              chartProjections: {
                cashOnCash: cashOnCashData,
                totalROI: totalROIData
              }
            };
            
            // Update the property document
            await updateDoc(propertyRef, {
              scenarioProjections: existingProjections,
              updatedAt: new Date().toISOString()
            });
          }
        } catch (propertyError) {
          console.error('Error updating property with scenario projections:', propertyError);
          // Continue with scenario save even if property update fails
        }
      }

      // Update the UI with new calculations after successful save
      setCalculatedValues(newCalculatedValues);
      
      // Update the scenario state with the new calculations to refresh the UI
      setScenario(prevScenario => ({
        ...prevScenario,
        calculations: newCalculatedValues.calculations,
        cashToClose: totalCashInvested,
        chartProjections: {
          cashOnCash: cashOnCashData,
          totalROI: totalROIData
        }
      }));
      
      setIsModified(false);
    } catch (error) {
      console.error('Error saving scenario:', error);
      setError(`Failed to save scenario: ${error.message}. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = () => {
    setIsModified(true);
  };
  
  // Handle form submission when Enter key is pressed
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isModified && !isLoading && !isSaving) {
      e.preventDefault();
      handleSave();
    }
  };
  
  // Effect to update chart projections after table data is calculated
  useEffect(() => {
    // We don't run this on initial render, only after needsChartUpdate is set to true
    // and the table data has been populated
    if (needsChartUpdate && 
        tableDataRef.current && 
        tableDataRef.current.cashFlowPercentByYear.some(val => val !== 0) && 
        tableDataRef.current.cumulativeROIByYear.some(val => val !== 0)) {
      // We use setTimeout to ensure all rows have been processed
      const timer = setTimeout(() => {
        updateChartProjections();
        // Set needsChartUpdate to false to ensure this only runs once
        setNeedsChartUpdate(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [needsChartUpdate, id]);

  // Function to update scenario name
  const handleUpdateScenarioName = async (newName) => {
    try {
      const docRef = doc(db, 'scenarios', id);
      await updateDoc(docRef, {
        name: newName,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setName(newName);
      setScenario(prev => ({ ...prev, name: newName }));
      
      // If this scenario is linked to a property, update the name in the property's scenarioProjections
      if (scenario.houseId) {
        try {
          const propertyRef = doc(db, 'properties', scenario.houseId);
          const propertyDoc = await getDoc(propertyRef);
          
          if (propertyDoc.exists()) {
            // Get existing scenarios projections
            const existingProjections = propertyDoc.data().scenarioProjections || {};
            
            // Update this scenario's name if it exists in projections
            if (existingProjections[id]) {
              existingProjections[id].name = newName;
              
              // Update the property document
              await updateDoc(propertyRef, {
                scenarioProjections: existingProjections,
                updatedAt: new Date().toISOString()
              });
            }
          }
        } catch (propertyError) {
          console.error('Error updating property with new scenario name:', propertyError);
          // Continue even if property update fails
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error updating scenario name:', error);
      throw error;
    }
  };

  const handleApplyPrice = (newPrice, newDownPayment, newInterestRate, newClosingCosts, newMonthlyRent) => {
    if (newPrice !== undefined) {
      priceFormat.onChange({ target: { value: newPrice.toString() } });
    }
    
    if (newDownPayment !== undefined) {
      setDownPaymentPercent(newDownPayment.toString());
    }
    
    if (newInterestRate !== undefined) {
      setInterestRate(newInterestRate.toString());
    }
    
    if (newClosingCosts !== undefined) {
      closingCostsFormat.onChange({ target: { value: newClosingCosts.toString() } });
    }
    
    if (newMonthlyRent !== undefined) {
      monthlyRentFormat.onChange({ target: { value: newMonthlyRent.toString() } });
    }
    
    setIsModified(true);
  };

  // Helper function to handle percentage input changes
  const handlePercentageChange = (e, setValue) => {
    const value = e.target.value.replace(/%/g, '');
    setValue(value);
    handleInputChange();
  };

  const handlePercentageFocus = (name) => {
    setIsFocused(prev => ({ ...prev, [name]: true }));
  };

  const handlePercentageBlur = (name) => {
    setIsFocused(prev => ({ ...prev, [name]: false }));
  };

  // Update dollar input fields to use formatted value directly
  const handleFormattedChange = (e, formatter) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, '');
    formatter.onChange(e);
    handleInputChange();
  };

  // Generate and download PDF report
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add title and property info
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text(scenario.name, pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    if (scenario.house) {
      doc.text(`${scenario.house.address}, ${scenario.house.location}`, pageWidth / 2, 30, { align: 'center' });
    }
    
    // Add date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth - 15, 40, { align: 'right' });
    
    // Key Metrics Section
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Key Metrics', 14, 50);
    
    doc.setFontSize(11);
    const metricsData = [
      ['Total ROI (Year 1)', `${(calculatedValues.calculations?.annualROI || 0).toFixed(2)}%`],
      ['Cash on Cash Return', `${(calculatedValues.calculations?.annualCashOnCashReturn || 0).toFixed(2)}%`],
      ['Cap Rate', `${(calculatedValues.calculations?.capRate || 0).toFixed(2)}%`],
      ['Monthly Cash Flow', `$${(calculatedValues.calculations?.annualCashFlow / 12 || 0).toFixed(2)}`]
    ];
    
    autoTable(doc, {
      startY: 55,
      head: [['Metric', 'Value']],
      body: metricsData,
      theme: 'grid',
      headStyles: { fillColor: [66, 66, 66] },
      alternateRowStyles: { fillColor: [240, 240, 240] }
    });
    
    // Mortgage Details Section
    const metricsTableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 55;
    doc.text('Mortgage Details', 14, metricsTableEndY + 15);
    
    const mortgageData = [
      ['Purchase Price', `$${Number(priceFormat.value || 0).toLocaleString()}`],
      ['Cash Purchase', isCashPurchase ? 'Yes' : 'No']
    ];
    
    if (!isCashPurchase) {
      mortgageData.push(
        ['Down Payment', `${downPaymentPercent}%`],
        ['Interest Rate', `${interestRate}%`],
        ['Loan Term', `${term} years`]
      );
    }
    
    mortgageData.push(
      ['Closing Costs', `$${Number(closingCostsFormat.value || 0).toLocaleString()}`],
      ['Monthly Mortgage', `$${calculatedValues.monthlyMortgage.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
      ['Cash to Close', `$${calculatedValues.cashToClose.toLocaleString()}`]
    );
    
    const previousTableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 55;
    autoTable(doc, {
      startY: previousTableEndY + 20,
      head: [['Item', 'Value']],
      body: mortgageData,
      theme: 'grid',
      headStyles: { fillColor: [66, 66, 66] },
      alternateRowStyles: { fillColor: [240, 240, 240] }
    });
    
    // Income & Expenses Section
    const mortgageTableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 55;
    doc.text('Income & Expenses', 14, mortgageTableEndY + 15);
    
    const incomeExpenseData = [
      ['Monthly Rent', `$${Number(monthlyRentFormat.value || 0).toLocaleString()}`],
      ['Annual Gross Rent', `$${calculatedValues.calculations?.annualGrossRent?.toLocaleString()}`],
      ['Annual Mortgage', `$${calculatedValues.calculations?.annualMortgage?.toLocaleString()}`],
      ['Annual Insurance', `$${calculatedValues.calculations?.annualInsurance?.toLocaleString()}`],
      ['Annual Property Tax', `$${calculatedValues.calculations?.annualTaxes?.toLocaleString()}`],
      ['Annual Management', `$${calculatedValues.calculations?.annualManagement?.toLocaleString()}`],
      ['Annual Vacancy', `$${calculatedValues.calculations?.annualVacancy?.toLocaleString()}`],
      ['Annual Maintenance', `$${calculatedValues.calculations?.annualMaintenance?.toLocaleString()}`],
      ['Total Annual Expenses', `$${calculatedValues.calculations?.annualExpenses?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
      ['Total Cash Flow', `$${calculatedValues.calculations?.annualCashFlow?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]
    ];
    
    const incomeTableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 55;
    autoTable(doc, {
      startY: incomeTableEndY + 20,
      head: [['Item', 'Value']],
      body: incomeExpenseData,
      theme: 'grid',
      headStyles: { fillColor: [66, 66, 66] },
      alternateRowStyles: { fillColor: [240, 240, 240] }
    });
    
    // Valuation & Appreciation Section
    const expensesTableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 55;
    doc.text('Valuation & Appreciation', 14, expensesTableEndY + 15);
    
    const valuationData = [
      ['Current Market Value/ARV', `$${Number(homeValueFormat.value || 0).toLocaleString()}`],
      ['Repair Estimates', `$${Number(repairEstimatesFormat.value || 0).toLocaleString()}`],
      ['Rent Growth Rate', `${rentGrowth}%`],
      ['Appreciation Rate', `${appreciation}%`]
    ];
    
    const valuationTableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 55;
    autoTable(doc, {
      startY: valuationTableEndY + 20,
      head: [['Item', 'Value']],
      body: valuationData,
      theme: 'grid',
      headStyles: { fillColor: [66, 66, 66] },
      alternateRowStyles: { fillColor: [240, 240, 240] }
    });
    
    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text('RE-Calc Scenario Report', 14, doc.internal.pageSize.getHeight() - 10);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
    }
    
    // Save the PDF
    doc.save(`${scenario.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_scenario.pdf`);
  };

  if (isLoading || isSaving) return <LoadingSpinner />;
  if (error) return (
    <div className="alert alert-danger">
      <h4>Error</h4>
      <p>{error}</p>
    </div>
  );

  return (
    <div className="container-fluid py-3 scenario-view" onKeyDown={handleKeyDown}>
      <style>
        {`
          .scenario-view .form-label {
            font-size: 0.85rem;
            margin-bottom: 0.25rem;
          }
          .scenario-view .form-control,
          .scenario-view .input-group-text {
            font-size: 0.875rem;
            padding: 0.25rem 0.5rem;
          }
          .scenario-view .card-title {
            font-size: 0.95rem;
          }
          .scenario-view .text-muted {
            font-size: 0.85rem;
          }
          .scenario-view table {
            font-size: 0.875rem;
          }
          .scenario-view table th {
            font-size: 0.8rem;
            font-weight: 600;
          }
          .scenario-view .key-metrics {
            font-size: 0.875rem;
          }
          .scenario-view .key-metrics .metric-value {
            font-size: 1.1rem;
          }
          .scenario-view .btn {
            font-size: 0.875rem;
            padding: 0.25rem 0.75rem;
          }
        `}
      </style>
      <Breadcrumb propertyId={scenario.houseId} scenarioId={id} />

      {/* Header Section */}
      <div className="row mb-4 align-items-start">
        <div className="col">
          <h1 className="mb-2">{scenario.name}</h1>
          {scenario.house && (
            <p className="text-muted mb-2">
              {scenario.house.address}, {scenario.house.location}
            </p>
          )}
        </div>
        <div className="col-auto">
          {/* <button
            className="btn btn-outline-secondary btn-sm me-2"
            onClick={generatePDF}
          >
            <i className="bi bi-file-earmark-pdf me-2"></i>Save as PDF
          </button> */}
          <button
            className="btn btn-outline-secondary btn-sm me-2"
            onClick={() => setShowEditNameModal(true)}
          >
            <i className="bi bi-pencil me-2"></i>Edit Scenario
          </button>
          <button
            className="btn btn-outline-primary btn-sm me-2"
            onClick={() => setShowGoalSeekModal(true)}
          >
            <i className="bi bi-bullseye me-2"></i>Goal Seek
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowCloneModal(true)}
          >
            <i className="bi bi-files me-2"></i>Clone Scenario
          </button>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {/* Monthly Cash Flow */}
        <div className="col">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2 px-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center mb-1">
                <i className="bi bi-cash-stack me-2 text-muted"></i>
                <span className="text-muted small">MONTHLY CASH FLOW</span>
              </div>
              <div className="d-flex align-items-center mb-0">
                <span className={`fs-4 fw-bold ${scenario.calculations?.annualCashFlow / 12 > 0 ? 'text-success' : 'text-danger'}`}>
                  {(() => {
                    const monthlyCashFlow = scenario.calculations?.annualCashFlow / 12;
                    if (monthlyCashFlow < 0) {
                      return `-$${Math.abs(monthlyCashFlow).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    } else {
                      return `$${monthlyCashFlow.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    }
                  })()}
                </span>
              </div>
              <div className="text-muted small">
                <span title="Total investment (cash to close + repairs)">
                  Inv: ${(Number(repairEstimatesFormat.value || 0) + calculatedValues.cashToClose || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Cash on Cash Return */}
        <div className="col">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2 px-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center mb-1">
                <i className="bi bi-currency-dollar me-2 text-muted"></i>
                <span className="text-muted small">CASH ON CASH</span>
              </div>
              <div className="d-flex align-items-center mb-0">
                <span className={`fs-4 fw-bold ${(propertyTargets.targetCoCReturn && scenario.calculations?.annualCashOnCashReturn >= Number(propertyTargets.targetCoCReturn)) || 
                  (!propertyTargets.targetCoCReturn && scenario.calculations?.annualCashOnCashReturn > 0) ? 'text-success' : 'text-danger'}`}>
                  {(scenario.calculations?.annualCashOnCashReturn || 0).toFixed(2)}%
                </span>
              </div>
              <div className="text-muted small">
                <span title="Target Cash on Cash Return">
                  Target: {propertyTargets.targetCoCReturn ? `${propertyTargets.targetCoCReturn}%` : 'Not set'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Total ROI */}
        <div className="col">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2 px-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center mb-1">
                <i className="bi bi-graph-up me-2 text-muted"></i>
                <span className="text-muted small">TOTAL ROI</span>
              </div>
              <div className="d-flex align-items-center mb-0">
                <span className={`fs-4 fw-bold ${(propertyTargets.targetTotalROI && scenario.calculations?.annualROI >= Number(propertyTargets.targetTotalROI)) || 
                  (!propertyTargets.targetTotalROI && scenario.calculations?.annualROI > 0) ? 'text-success' : 'text-danger'}`}>
                  {(scenario.calculations?.annualROI || 0).toFixed(2)}%
                </span>
              </div>
              <div className="text-muted small">
                <span title="Target Total ROI">
                  Target: {propertyTargets.targetTotalROI ? `${propertyTargets.targetTotalROI}%` : 'Not set'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Cap Rate */}
        <div className="col">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2 px-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center mb-1">
                <i className="bi bi-house me-2 text-muted"></i>
                <span className="text-muted small">CAP RATE</span>
              </div>
              <div className="d-flex align-items-center mb-0">
                <span className={`fs-4 fw-bold ${(propertyTargets.targetCapRate && scenario.calculations?.capRate >= Number(propertyTargets.targetCapRate)) || 
                  (!propertyTargets.targetCapRate && scenario.calculations?.capRate > 0) ? 'text-success' : 'text-danger'}`}>
                  {(scenario.calculations?.capRate || 0).toFixed(2)}%
                </span>
              </div>
              <div className="text-muted small">
                <span title="Target Cap Rate">
                  Target: {propertyTargets.targetCapRate ? `${propertyTargets.targetCapRate}%` : 'Not set'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* DSCR */}
        <div className="col">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2 px-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center mb-1">
                <i className="bi bi-bank me-2 text-muted"></i>
                <span className="text-muted small">DSCR</span>
              </div>
              <div className="d-flex align-items-center mb-0">
                <span className={`fs-4 fw-bold ${scenario.calculations?.dscr === null ? 'text-muted' : (scenario.calculations?.dscr >= 1.2 ? 'text-success' : 'text-danger')}`}>
                  {scenario.calculations?.dscr === null ? '--' : (scenario.calculations?.dscr || 0).toFixed(2)}
                </span>
              </div>
              <div className="text-muted small">
                <span title="Vacancy rate used in calculations">Vacancy: {vacancyRate || '0'}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row g-3 mb-3">
        {/* Property Details & Financing Card */}
        <div className="col-md-6">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2">
              <h6 className="card-title mb-2 text-white">Purchase Details</h6>
              <div className="row g-2">
                {/* Two-column layout */}
                <div className="col-md-6">
                  <label className="form-label">Purchase Price</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={priceFormat.formatted}
                      onChange={(e) => handleFormattedChange(e, priceFormat)}
                      placeholder="Enter price"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Purchase Method</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="cashPurchaseToggle"
                      checked={isCashPurchase}
                      onChange={() => {
                        setIsCashPurchase(!isCashPurchase);
                        if (!isCashPurchase) {
                          // When enabling cash purchase, set down payment to 100%
                          setDownPaymentPercent('100');
                        }
                        handleInputChange();
                      }}
                    />
                    <label className="form-check-label text-muted" htmlFor="cashPurchaseToggle">
                      Cash Purchase
                    </label>
                  </div>
                </div>

                {/* Financing fields - hidden when Cash Purchase is toggled */}
                {!isCashPurchase && (
                  <>
                    {/* Left column */}
                    <div className="col-md-6">
                      <label className="form-label">Down Payment (%)</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control bg-dark text-light border-secondary"
                          value={downPaymentPercent}
                          onChange={(e) => {
                            handlePercentageChange(e, setDownPaymentPercent);
                            handleInputChange();
                          }}
                          placeholder="Enter down payment %"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>

                    {/* Right column */}
                    <div className="col-md-6">
                      <label className="form-label">Loan Term (Years)</label>
                      <select
                        className="form-select form-select-sm bg-dark text-light border-secondary"
                        value={term}
                        onChange={(e) => {
                          setTerm(e.target.value);
                          handleInputChange();
                        }}
                        style={{ height: "30px", fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                      >
                        <option value="15">15 Years</option>
                        <option value="20">20 Years</option>
                        <option value="30">30 Years</option>
                      </select>
                    </div>

                    {/* Left column */}
                    <div className="col-md-6">
                      <label className="form-label">Interest Rate (%)</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control bg-dark text-light border-secondary"
                          value={interestRate}
                          onChange={(e) => handlePercentageChange(e, setInterestRate)}
                          placeholder="Enter interest rate"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Right column */}
                <div className="col-md-6">
                  <label className="form-label">Closing Costs</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={closingCostsFormat.formatted}
                      onChange={(e) => handleFormattedChange(e, closingCostsFormat)}
                      placeholder="Enter closing costs"
                    />
                  </div>
                </div>
              </div>
              
              {/* Bottom Row - Visual Summary */}
              <div className="border-top border-secondary mt-3 pt-1">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Monthly Mortgage</label>
                    <div className="d-flex justify-content-end">
                      <span className="me-1 fw-bold" style={{ color: isCashPurchase ? '#6c757d' : 'white' }}>$</span>
                      <span className="fw-bold" style={{ color: isCashPurchase ? '#6c757d' : 'white' }}>
                        {isCashPurchase ? '0.00' : calculatedValues.monthlyMortgage?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Cash to Close</label>
                    <div className="d-flex justify-content-end">
                      <span className="me-1 fw-bold text-white">$</span>
                      <span className="fw-bold text-white">
                        {isCashPurchase 
                          ? (Number(priceFormat.value || 0) + Number(closingCostsFormat.value || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                          : (Number(priceFormat.value || 0) * (Number(downPaymentPercent || 0) / 100) + Number(closingCostsFormat.value || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Expenses Card */}
        <div className="col-md-6">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2">
              <h6 className="card-title mb-2 text-white">Expenses</h6>
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label">Monthly Insurance</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={monthlyInsuranceFormat.formatted}
                      onChange={(e) => handleFormattedChange(e, monthlyInsuranceFormat)}
                      placeholder="Enter monthly insurance"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Annual Property Tax</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={propertyTaxFormat.formatted}
                      onChange={(e) => handleFormattedChange(e, propertyTaxFormat)}
                      placeholder="Enter annual property tax"
                    />
                  </div>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Management Fee (%)</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={managementFee}
                      onChange={(e) => handlePercentageChange(e, setManagementFee)}
                      placeholder="Enter management fee"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Maintenance Rate (%)</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={maintenanceRate}
                      onChange={(e) => handlePercentageChange(e, setMaintenanceRate)}
                      placeholder="Enter maintenance rate"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">CapEx Reserves (%)</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={capExRate}
                      onChange={(e) => handlePercentageChange(e, setCapExRate)}
                      placeholder="Enter CapEx reserves"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Other Misc. Expenses (Monthly)</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={otherExpensesFormat.formatted}
                      onChange={(e) => handleFormattedChange(e, otherExpensesFormat)}
                      placeholder="Enter monthly misc expenses"
                    />
                  </div>
                </div>
              </div>
              
              {/* Expenses Summary */}
              <div className="border-top border-secondary mt-3 pt-1">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Total Monthly Expenses</label>
                    <div className="d-flex justify-content-end">
                      <span className="me-1 fw-bold text-white">$</span>
                      <span className="fw-bold text-white">
                        {(() => {
                          // Base expenses (insurance, taxes, other)
                          const baseExpenses = Number(monthlyInsuranceFormat.value || 0) + 
                                           Number(propertyTaxFormat.value || 0) / 12 + 
                                           Number(otherExpensesFormat.value || 0);
                          
                          // Get monthly rent (or equivalent for short-term)
                          const monthlyRent = rentalType === 'long-term' 
                            ? Number(monthlyRentFormat.value || 0)
                            : (Number(nightlyRateFormat.value || 0) * 365 * Number(occupancyRate || 0) / 100) / 12;
                          
                          // Calculate percentage-based expenses from gross potential income
                          const grossPotentialIncome = rentalType === 'long-term'
                            ? Number(monthlyRentFormat.value || 0)
                            : (Number(nightlyRateFormat.value || 0) * 365) / 12;
                            
                          const managementExpense = grossPotentialIncome * (Number(managementFee || 0) / 100);
                          const maintenanceExpense = grossPotentialIncome * (Number(maintenanceRate || 0) / 100);
                          const capExExpense = grossPotentialIncome * (Number(capExRate || 0) / 100);
                          
                          // Add all expenses together
                          const totalMonthlyExpenses = baseExpenses + managementExpense + maintenanceExpense + capExExpense;
                          
                          return totalMonthlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Total Annual Expenses</label>
                    <div className="d-flex justify-content-end">
                      <span className="me-1 fw-bold text-white">$</span>
                      <span className="fw-bold text-white">
                        {(() => {
                          // Base annual expenses (insurance, taxes, other)
                          const baseAnnualExpenses = Number(monthlyInsuranceFormat.value || 0) * 12 + 
                                                Number(propertyTaxFormat.value || 0) + 
                                                Number(otherExpensesFormat.value || 0) * 12;
                          
                          // Get annual rent (or equivalent for short-term)
                          const annualRent = rentalType === 'long-term' 
                            ? Number(monthlyRentFormat.value || 0) * 12
                            : Number(nightlyRateFormat.value || 0) * 365 * Number(occupancyRate || 0) / 100;
                          
                          // Calculate percentage-based expenses from gross potential income
                          const grossPotentialIncome = rentalType === 'long-term'
                            ? Number(monthlyRentFormat.value || 0) * 12
                            : Number(nightlyRateFormat.value || 0) * 365;
                            
                          const managementExpense = grossPotentialIncome * (Number(managementFee || 0) / 100);
                          const maintenanceExpense = grossPotentialIncome * (Number(maintenanceRate || 0) / 100);
                          const capExExpense = grossPotentialIncome * (Number(capExRate || 0) / 100);
                          
                          // Add all expenses together
                          const totalAnnualExpenses = baseAnnualExpenses + managementExpense + maintenanceExpense + capExExpense;
                          
                          return totalAnnualExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Rental Income Card */}
        <div className="col-md-6">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2">
              <h6 className="card-title mb-2 text-white">Rental Income</h6>
              
              {/* Rental Type Selection */}
              <div className="mb-3">
                <label className="form-label">Rental Type</label>
                <div className="d-flex">
                  <div className="form-check me-4">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="rentalType"
                      id="longTerm"
                      value="long-term"
                      checked={rentalType === 'long-term'}
                      onChange={() => {
                        setRentalType('long-term');
                        handleInputChange();
                      }}
                    />
                    <label className="form-check-label text-muted" htmlFor="longTerm">
                      Long-term Rental
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="rentalType"
                      id="shortTerm"
                      value="short-term"
                      checked={rentalType === 'short-term'}
                      onChange={() => {
                        setRentalType('short-term');
                        handleInputChange();
                      }}
                    />
                    <label className="form-check-label text-muted" htmlFor="shortTerm">
                      Short-term Rental
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="row g-2 mb-3">
                {/* Long-term rental inputs */}
                {rentalType === 'long-term' && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Monthly Rent</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="text"
                          className="form-control bg-dark text-light border-secondary"
                          value={monthlyRentFormat.formatted}
                          onChange={(e) => handleFormattedChange(e, monthlyRentFormat)}
                          placeholder="Enter monthly rent"
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">Vacancy Rate (%)</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control bg-dark text-light border-secondary"
                          value={vacancyRate}
                          onChange={(e) => handlePercentageChange(e, setVacancyRate)}
                          placeholder="Enter vacancy rate"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Short-term rental inputs */}
                {rentalType === 'short-term' && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Nightly Rate</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="text"
                          className="form-control bg-dark text-light border-secondary"
                          value={nightlyRateFormat.formatted}
                          onChange={(e) => handleFormattedChange(e, nightlyRateFormat)}
                          placeholder="Enter nightly rate"
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">Occupancy Rate (%)</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control bg-dark text-light border-secondary"
                          value={occupancyRate}
                          onChange={(e) => {
                            handlePercentageChange(e, setOccupancyRate);
                            handleInputChange();
                          }}
                          placeholder="Enter occupancy rate"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              {/* Gross Income Summary */}
              <div className="border-top border-secondary mt-3 pt-3">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Total Income (less vacancy)</label>
                    <div className="d-flex justify-content-end">
                      <span className="me-1 fw-bold text-white">$</span>
                      <span className="fw-bold text-white">
                        {(() => {
                          let annualGross = 0;
                          if (rentalType === 'long-term') {
                            // Calculate annual rent less vacancy for long-term rentals
                            const monthlyRent = Number(monthlyRentFormat.value || 0);
                            const vacancyRateDecimal = Number(vacancyRate || 0) / 100;
                            annualGross = monthlyRent * 12 * (1 - vacancyRateDecimal);
                          } else {
                            // Calculate annual rent for short-term rentals based on occupancy
                            const nightlyRate = Number(nightlyRateFormat.value || 0);
                            const occupancyRateDecimal = Number(occupancyRate || 0) / 100;
                            annualGross = nightlyRate * 365 * occupancyRateDecimal;
                          }
                          return annualGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label">Gross Potential Income (100% occupancy)</label>
                    <div className="d-flex justify-content-end">
                      <span className="me-1 fw-bold text-white">$</span>
                      <span className="fw-bold text-white">
                        {(() => {
                          let grossPotentialIncome = 0;
                          if (rentalType === 'long-term') {
                            // Calculate annual rent at 100% occupancy for long-term rentals
                            const monthlyRent = Number(monthlyRentFormat.value || 0);
                            grossPotentialIncome = monthlyRent * 12;
                          } else {
                            // Calculate annual rent at 100% occupancy for short-term rentals
                            const nightlyRate = Number(nightlyRateFormat.value || 0);
                            grossPotentialIncome = nightlyRate * 365;
                          }
                          return grossPotentialIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </span>
                    </div>
                    <div className="text-end">
                      <small className="text-muted fst-italic">Base for percentage-based expenses</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        


        {/* Valuation & Appreciation Card */}
        <div className="col-md-6">
          <div className="card bg-dark border-secondary h-100">
            <div className="card-body py-2">
              <h6 className="card-title mb-2 text-white">Valuation & Appreciation</h6>
              
              {/* Appreciation Section */}
              <h6 className="text-muted mb-2">Appreciation</h6>
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Appreciation Rate (%)</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={appreciation}
                      onChange={(e) => handlePercentageChange(e, setAppreciation)}
                      placeholder="Enter appreciation rate"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Rent Growth Rate (%)</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={rentGrowth}
                      onChange={(e) => handlePercentageChange(e, setRentGrowth)}
                      placeholder="Enter rent growth"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
              </div>
              
              {/* Valuation Section */}
              <h6 className="text-muted mb-2">Valuation</h6>
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label">Repair Estimates</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={repairEstimatesFormat.formatted}
                      onChange={(e) => handleFormattedChange(e, repairEstimatesFormat)}
                      placeholder="Enter repair estimates"
                    />
                  </div>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Current Market Value/ARV</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      value={homeValueFormat.formatted}
                      onChange={(e) => handleFormattedChange(e, homeValueFormat)}
                      placeholder="Enter ARV"
                    />
                  </div>
                  <div className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="useArvForCapRate"
                      checked={useArvForCapRate}
                      onChange={(e) => {
                        setUseArvForCapRate(e.target.checked);
                        handleInputChange();
                      }}
                    />
                    <label className="form-check-label text-muted" htmlFor="useArvForCapRate">
                      Use ARV for Cap Rate
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        
      {/* Yearly Returns Table */}
      <div className="row mt-3">
        <div className="col-12">
          <div className="card bg-dark border-secondary">
            <div className="card-body py-2">
              <h6 className="card-title mb-2 text-white">Yearly Returns</h6>
              
              {/* Returns Table */}
              <div className="table-responsive">
                <table className="table table-dark mb-0">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Property Value</th>
                      <th>{rentalType === 'short-term' ? 'Annual Income' : 'Monthly Rent'}</th>
                      <th>Appreciation %</th>
                      <th>Cash Flow %</th>
                      <th>Debt Paydown %</th>
                      <th>Yearly ROI</th>
                      <th>Total ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: yearsPerPage }).map((_, i) => {
                      // No need to create a temporary copy anymore since we're using a ref
                      // We'll update the ref directly
                      const year = ((currentPage - 1) * yearsPerPage) + i + 1;
                      
                      // Calculate metrics for each year
                      const initialValue = Number(homeValueFormat.value) || Number(priceFormat.value) || 0;
                      const appreciationRate = Number(appreciation) / 100;
                      const rentGrowthRate = Number(rentGrowth) / 100;
                      const totalCashInvested = (Number(priceFormat.value || 0) * (Number(downPaymentPercent || 0) / 100)) + 
                                         Number(closingCostsFormat.value || 0) +
                                         Number(repairEstimatesFormat.value || 0);

                      // Calculate market rent for this year based on rental type
                      let yearlyRent = 0;
                      if (rentalType === 'short-term') {
                        // For short-term rentals, calculate based on nightly rate and occupancy rate
                        const nightlyRate = Number(nightlyRateFormat.value || 0);
                        const occupancyRateDecimal = Number(occupancyRate || 65) / 100;
                        const estimatedOccupancyDays = 365 * occupancyRateDecimal;
                        yearlyRent = nightlyRate * estimatedOccupancyDays;
                      } else {
                        // For long-term rentals, simply multiply monthly rent by 12
                        yearlyRent = Number(monthlyRentFormat.value || 0) * 12;
                      }
                      
                      let yearlyInsurance = Number(monthlyInsuranceFormat.value) * 12;
                      let yearlyTaxes = Number(propertyTaxFormat.value);
                      
                      // Compound rent, insurance, and taxes by growth rate
                      for (let y = 1; y < year; y++) {
                        yearlyRent *= (1 + rentGrowthRate);
                        yearlyInsurance *= (1 + rentGrowthRate);
                        yearlyTaxes *= (1 + rentGrowthRate);
                      }

                      // Calculate appreciation for this specific year
                      let previousYearValue = initialValue;
                      for (let y = 1; y < year; y++) {
                        previousYearValue *= (1 + appreciationRate);
                      }
                      const currentYearValue = previousYearValue * (1 + appreciationRate);
                      const yearlyAppreciationAmount = currentYearValue - previousYearValue;
                      const appreciationPercent = totalCashInvested > 0 ? (yearlyAppreciationAmount / totalCashInvested) * 100 : 0;
                      
                      // Calculate future value (still needed for display)
                      const futureValue = currentYearValue;
                      
                      // Calculate metrics using the compounded rent and expenses
                      let yearlyNOI = 0;
                                            // Calculate percentage-based expenses based on total gross rent (before vacancy)
                       const yearlyManagement = yearlyRent * (Number(managementFee) / 100);
                       const yearlyMaintenance = yearlyRent * (Number(maintenanceRate) / 100);
                       const yearlyCapEx = yearlyRent * (Number(capExRate) / 100);
                       
                       if (rentalType === 'short-term') {
                        // For short-term rentals, we already accounted for occupancy in the yearlyRent calculation
                        yearlyNOI = yearlyRent - 
                                    yearlyManagement - 
                                    yearlyMaintenance -
                                    yearlyCapEx -
                                    yearlyInsurance -
                                    yearlyTaxes;
                      } else {
                        // For long-term rentals, calculate vacancy separately
                        const yearlyVacancy = yearlyRent * (Number(vacancyRate) / 100);
                        
                        // Calculate NOI by subtracting all expenses from gross rent
                        yearlyNOI = yearlyRent - 
                                    yearlyVacancy - 
                                    yearlyManagement - 
                                    yearlyMaintenance -
                                    yearlyCapEx -
                                    yearlyInsurance -
                                    yearlyTaxes;
                      }
                      const yearlyMortgage = calculatedValues.monthlyMortgage * 12;
                      const cashFlow = yearlyNOI - yearlyMortgage;
                      const cashFlowPercent = totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : 0;
                      
                      // We'll store the values directly in the ref when we calculate them
                      // No need for intermediate storage
                      
                      // Calculate debt paydown using amortization schedule
                      const loanAmount = Number(priceFormat.value || 0) * (1 - Number(downPaymentPercent || 0) / 100);
                      const monthlyRate = (Number(interestRate || 0) / 100) / 12;
                      const monthlyPayment = calculatedValues.monthlyMortgage;

                      let remainingBalance = loanAmount;
                      let startOfYearBalance = loanAmount;
                      
                      // Fast forward to current year's amortization
                      for (let month = 1; month <= (year - 1) * 12; month++) {
                        const interestPayment = remainingBalance * monthlyRate;
                        const principalPayment = monthlyPayment - interestPayment;
                        remainingBalance = remainingBalance - principalPayment;
                      }
                      startOfYearBalance = remainingBalance;
                      
                      // Calculate this year's principal paydown
                      for (let month = 1; month <= 12; month++) {
                        const interestPayment = remainingBalance * monthlyRate;
                        const principalPayment = monthlyPayment - interestPayment;
                        remainingBalance = remainingBalance - principalPayment;
                      }
                      
                      const yearlyPrincipalPaydown = startOfYearBalance - remainingBalance;
                      const principalPaydownPercent = totalCashInvested > 0 ? (yearlyPrincipalPaydown / totalCashInvested) * 100 : 0;
                      

                      const yearlyROI = appreciationPercent + cashFlowPercent + principalPaydownPercent;

                      // Calculate cumulative ROI
                      let cumulativeROI = 0;
                      for (let y = 1; y <= year; y++) {
                        // Calculate appreciation for this year
                        let prevValue = initialValue;
                        for (let py = 1; py < y; py++) {
                          prevValue *= (1 + appreciationRate);
                        }
                        const currValue = prevValue * (1 + appreciationRate);
                        const yearAppreciation = currValue - prevValue;
                        const yearAppreciationPercent = totalCashInvested > 0 ? (yearAppreciation / totalCashInvested) * 100 : 0;

                        // Calculate cash flow percent (same each year)
                        const yearCashFlowPercent = cashFlowPercent;

                        // Calculate debt paydown for this year
                        let yearRemBalance = loanAmount;
                        let yearStartBalance = loanAmount;
                        
                        // Fast forward to this year
                        for (let month = 1; month <= (y - 1) * 12; month++) {
                          const intPayment = yearRemBalance * monthlyRate;
                          const princPayment = monthlyPayment - intPayment;
                          yearRemBalance = yearRemBalance - princPayment;
                        }
                        yearStartBalance = yearRemBalance;
                        
                        // Calculate this year's paydown
                        for (let month = 1; month <= 12; month++) {
                          const intPayment = yearRemBalance * monthlyRate;
                          const princPayment = monthlyPayment - intPayment;
                          yearRemBalance = yearRemBalance - princPayment;
                        }
                        
                        const yearPrincPaydown = yearStartBalance - yearRemBalance;
                        const yearPaydownPercent = totalCashInvested > 0 ? (yearPrincPaydown / totalCashInvested) * 100 : 0;

                        // Add this year's ROI to cumulative
                        cumulativeROI += yearAppreciationPercent + yearCashFlowPercent + yearPaydownPercent;
                        
                        // We'll store the values directly in the ref when we calculate them
                        // No need for intermediate storage
                      }

                      // Store the calculated values in our ref for later use when saving
                      // This won't cause re-renders since we're using a ref
                      tableDataRef.current.cashFlowPercentByYear[year - 1] = Number(cashFlowPercent.toFixed(2));
                      tableDataRef.current.cumulativeROIByYear[year - 1] = Number(cumulativeROI.toFixed(2));
                      
                      // If this is the last row of the first page and we need to update chart projections
                      // We use setTimeout to ensure all rows have been processed
                      if (year === yearsPerPage && needsChartUpdate && i === yearsPerPage - 1) {
                        setTimeout(() => {
                          updateChartProjections();
                          // Set needsChartUpdate to false to ensure this only runs once
                          setNeedsChartUpdate(false);
                        }, 500);
                      }
                      
                      return (
                        <tr key={year}>
                          <td className="text-white">{year}</td>
                          <td className="text-white">${futureValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="text-white">
                            {rentalType === 'short-term' 
                              ? `$${yearlyRent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                              : `$${(yearlyRent / 12).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            }
                          </td>
                          <td className="text-white">
                            <div className={`badge ${appreciationPercent > 0 ? 'bg-success' : appreciationPercent < 0 ? 'bg-danger' : 'bg-secondary'} fs-6`}>
                              {appreciationPercent.toFixed(2)}%
                            </div>
                          </td>
                          <td className="text-white">
                            <div className={`badge ${cashFlowPercent > 0 ? 'bg-success' : cashFlowPercent < 0 ? 'bg-danger' : 'bg-secondary'} fs-6`}>
                              {cashFlowPercent.toFixed(2)}%
                            </div>
                          </td>
                          <td className="text-white">
                            <div className={`badge ${principalPaydownPercent > 0 ? 'bg-success' : principalPaydownPercent < 0 ? 'bg-danger' : 'bg-secondary'} fs-6`}>
                              {principalPaydownPercent.toFixed(2)}%
                            </div>
                          </td>
                          <td className="text-white">
                            <div className={`badge ${yearlyROI > 0 ? 'bg-success' : yearlyROI < 0 ? 'bg-danger' : 'bg-secondary'} fs-6`}>
                              {yearlyROI.toFixed(2)}%
                            </div>
                          </td>
                          <td className="text-white">
                            <div className={`badge ${cumulativeROI > 0 ? 'bg-success' : cumulativeROI < 0 ? 'bg-danger' : 'bg-secondary'} fs-6`}>
                              {cumulativeROI.toFixed(2)}%
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted small">
                    Showing years {((currentPage - 1) * yearsPerPage) + 1} to {Math.min(currentPage * yearsPerPage, 30)} of 30
                  </div>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clone Modal */}
      <CloneScenarioModal 
        show={showCloneModal} 
        onHide={() => setShowCloneModal(false)} 
        scenarioData={{
          ...scenario,
          // Include the most recent form values
          price: parseFloat(priceFormat.value) || 0,
          downPaymentPercent: parseFloat(downPaymentPercent) || 0,
          interestRate: parseFloat(interestRate) || 0,
          loanTermYears: parseInt(term) || 30,
          
          isCashPurchase: isCashPurchase,
          closingCosts: parseFloat(closingCostsFormat.value) || 0,
          monthlyRent: parseFloat(monthlyRentFormat.value) || 0,
          rentGrowth: parseFloat(rentGrowth) || 0,
          initialHomeValue: parseFloat(homeValueFormat.value) || 0,
          repairEstimates: parseFloat(repairEstimatesFormat.value) || 0,
          appreciation: parseFloat(appreciation) || 0,
          monthlyInsurance: parseFloat(monthlyInsuranceFormat.value) || 0,
          propertyTax: parseFloat(propertyTaxFormat.value) || 0,
          managementFee: parseFloat(managementFee) || 0,
          vacancyRate: parseFloat(vacancyRate) || 0,
          maintenanceRate: parseFloat(maintenanceRate) || 0
        }}
      />

      {/* Goal Seek Modal */}
      {showGoalSeekModal && (
        <GoalSeekModal
          show={showGoalSeekModal}
          onHide={() => setShowGoalSeekModal(false)}
          scenarioData={{
            ...scenario,
            // Include the most recent form values
            price: parseFloat(priceFormat.value) || 0,
            downPaymentPercent: parseFloat(downPaymentPercent) || 0,
            interestRate: parseFloat(interestRate) || 0,
            loanTermYears: parseInt(term) || 30,
            isCashPurchase: isCashPurchase,
            closingCosts: parseFloat(closingCostsFormat.value) || 0,
            monthlyRent: parseFloat(monthlyRentFormat.value) || 0,
            rentGrowth: parseFloat(rentGrowth) || 0,
            initialHomeValue: parseFloat(homeValueFormat.value) || 0,
            repairEstimates: parseFloat(repairEstimatesFormat.value) || 0,
            appreciation: parseFloat(appreciation) || 0,
            monthlyInsurance: parseFloat(monthlyInsuranceFormat.value) || 0,
            propertyTax: parseFloat(propertyTaxFormat.value) || 0,
            managementFee: parseFloat(managementFee) || 0,
            vacancyRate: parseFloat(vacancyRate) || 0,
            maintenanceRate: parseFloat(maintenanceRate) || 0,
            // Include the calculated metrics
            calculatedROI: calculatedValues.calculations?.annualROI || 0,
            calculations: {
              annualROI: calculatedValues.calculations?.annualROI || 0,
              annualCashOnCashReturn: calculatedValues.calculations?.annualCashOnCashReturn || 0,
              capRate: calculatedValues.calculations?.capRate || 0
            }
          }}
          onApplyPrice={handleApplyPrice}
        />
      )}
      
      {/* Edit Scenario Name Modal */}
      {showEditNameModal && (
        <EditScenarioNameModal
          show={showEditNameModal}
          onHide={() => setShowEditNameModal(false)}
          currentName={name}
          onUpdateName={handleUpdateScenarioName}
        />
      )}

      {/* Fixed Save Banner */}
      {isModified && (
        <div className="position-fixed bottom-0 start-0 w-100 bg-dark border-top border-secondary" style={{ zIndex: 1030 }}>
          <div className="container-fluid px-4 py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                You have unsaved changes
              </div>
              <div>
                <button 
                  className="btn btn-outline-secondary me-2" 
                  onClick={() => window.location.reload()}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSave}
                  disabled={isLoading || isSaving}
                >
                  {isLoading || isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Edit Scenario Name Modal Component
const EditScenarioNameModal = ({ show, onHide, currentName, onUpdateName }) => {
  const [newName, setNewName] = useState(currentName);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      setError('Scenario name cannot be empty');
      return;
    }
    
    setIsUpdating(true);
    try {
      await onUpdateName(newName.trim());
      onHide();
    } catch (error) {
      setError(`Failed to update scenario name: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Handle click outside modal to close it
  const handleBackdropClick = (e) => {
    if (!isUpdating) {
      onHide();
    }
  };
  
  // Prevent clicks inside the modal from closing it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };
  
  return (
    <>
      <div className="modal-backdrop show" onClick={handleBackdropClick}></div>
      <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 1050 }} onClick={handleBackdropClick}>
        <div className="modal-dialog modal-dialog-centered" onClick={handleModalClick}>
          <div className="modal-content bg-dark text-light border-secondary">
            <div className="modal-header border-secondary">
              <h5 className="modal-title">Edit Scenario Name</h5>
              <button type="button" className="btn-close btn-close-white" onClick={onHide} disabled={isUpdating}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}
                <div className="mb-3">
                  <label htmlFor="scenarioName" className="form-label">
                    Scenario Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control bg-dark text-light ${!newName.trim() ? 'border-danger' : 'border-secondary'}`}
                    id="scenarioName"
                    value={newName}
                    onChange={(e) => {
                      setNewName(e.target.value);
                      if (e.target.value.trim()) {
                        setError('');
                      }
                    }}
                    onBlur={() => {
                      if (!newName.trim()) {
                        setError('Scenario name is required');
                      }
                    }}
                    placeholder="Enter scenario name"
                    required
                    autoFocus
                  />
                  {!newName.trim() && <div className="text-danger small mt-1">Scenario name is required</div>}
                </div>
              </div>
              <div className="modal-footer border-secondary">
                <button type="button" className="btn btn-outline-secondary" onClick={onHide} disabled={isUpdating}>Cancel</button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={isUpdating || !newName.trim()}
                >
                  {isUpdating ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Updating...</> : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScenarioView;
